import { baseApi } from "@/shared/api";
import type {
  TrialBalanceResponse,
  JournalEntriesResponse,
  JournalEntriesParams,
  JournalEntryDetail,
} from "../types/reports";

interface TrialBalanceParams {
  start_date: string;
  end_date: string;
  project_id?: number;
  status_filter?: string;
}

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrialBalance: builder.query<TrialBalanceResponse, TrialBalanceParams>({
      query(params) {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.status_filter) searchParams.set("status_filter", params.status_filter);
        return {
          url: `/api/accounting/trial-balance/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "Reports" as const, id: "TRIAL_BALANCE" }],
    }),

    getJournalEntries: builder.query<JournalEntriesResponse, JournalEntriesParams>({
      query(params) {
        const searchParams = new URLSearchParams();
        searchParams.set("entry_date__gte", params.start_date);
        searchParams.set("entry_date__lte", params.end_date);
        if (params.source_type) searchParams.set("source_type", params.source_type);
        if (params.status) searchParams.set("status", params.status);
        if (params.debit_account) searchParams.set("debit_account", params.debit_account);
        if (params.debit_account__startswith) searchParams.set("debit_account__startswith", params.debit_account__startswith);
        if (params.credit_account) searchParams.set("credit_account", params.credit_account);
        if (params.credit_account__startswith) searchParams.set("credit_account__startswith", params.credit_account__startswith);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.ordering) searchParams.set("ordering", params.ordering);
        if (params.lang) searchParams.set("lang", params.lang);
        return {
          url: `/api/accounting/journal-entries/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "Reports" as const, id: "JOURNAL_ENTRIES" }],
    }),

    getJournalEntryDetail: builder.query<JournalEntryDetail, number>({
      query(id) {
        return {
          url: `/api/accounting/journal-entries/${id}/`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags(_result, _error, id) {
        return [{ type: "Reports" as const, id: `JOURNAL_ENTRY_${id}` }];
      },
    }),
  }),
});

export const {
  useGetTrialBalanceQuery,
  useGetJournalEntriesQuery,
  useGetJournalEntryDetailQuery,
} = reportsApi;
